/**
 * CitationNetwork
 *
 * An interactive component for visualizing academic citation networks,
 * showing connections between research papers.
 *
 * Primary responsibilities:
 * - Display interconnected research papers and citations
 * - Visualize citation relationships and research influence
 * - Support exploration of academic networks and knowledge chains
 * - Allow identifying key papers and research clusters
 */

import React, { useState, useMemo, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { IconZoomIn, IconZoomOut, IconCrosshair, IconLink, IconBook } from '@tabler/icons-react'

export interface Publication {
  id: string
  title: string
  authors: string[]
  year: number
  venue: string
  abstract: string
  keywords: string[]
  doi?: string
  url?: string
  citations: string[] // IDs of papers this publication cites
  citedBy: string[] // IDs of papers that cite this publication
  metrics?: {
    // Optional metrics
    citations?: number
    impactFactor?: number
  }
}

export interface CitationNetworkData {
  title: string
  description: string
  publications: Publication[]
  focusedPublicationId?: string // ID of the publication to initially focus on
  maxDepth?: number // Maximum citation depth to explore (optional)
  layout?: 'force-directed' | 'circular' | 'grid' // Layout algorithm (optional)
  groupBy?: 'year' | 'venue' | 'keywords' | 'none' // Grouping criteria (optional)
}

// Utility Functions
const getShortId = (id: string) => id.substring(0, 6)

export const CitationNetwork: React.FC<{ data: CitationNetworkData }> = ({ data }) => {
  const [expandedPublications, setExpandedPublications] = useState<Set<string>>(() =>
    data.focusedPublicationId ? new Set([data.focusedPublicationId]) : new Set()
  )
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [focusedPublicationId, setFocusedPublicationId] = useState<string | null>(
    data.focusedPublicationId || null
  )
  const [depth, setDepth] = useState<number>(data.maxDepth || 2)
  const [showAbstracts, setShowAbstracts] = useState<boolean>(true)

  // Memoized search results
  const searchResults = useMemo(() => {
    if (!searchQuery) return data.publications

    const query = searchQuery.toLowerCase()
    return data.publications.filter(
      (pub) =>
        pub.title.toLowerCase().includes(query) ||
        pub.authors.some((author) => author.toLowerCase().includes(query)) ||
        pub.keywords.some((keyword) => keyword.toLowerCase().includes(query)) ||
        pub.venue.toLowerCase().includes(query)
    )
  }, [searchQuery, data.publications])

  // Memoized focused publication
  const focusedPublication = useMemo(() => {
    return data.publications.find((pub) => pub.id === focusedPublicationId) || null
  }, [focusedPublicationId, data.publications])

  // Toggle publication expansion
  const toggleExpand = useCallback((id: string) => {
    setExpandedPublications((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }, [])

  // Focus on a publication
  const focusPublication = useCallback(
    (id: string) => {
      setFocusedPublicationId(id)
      if (!expandedPublications.has(id)) {
        toggleExpand(id)
      }
    },
    [expandedPublications, toggleExpand]
  )

  // Reset focus
  const resetFocus = useCallback(() => {
    setFocusedPublicationId(null)
  }, [])

  // Get citing publications
  const getCitingPublications = useCallback(
    (publication: Publication): Publication[] => {
      return data.publications.filter((p) => publication.citedBy.includes(p.id))
    },
    [data.publications]
  )

  // Get cited publications
  const getCitedPublications = useCallback(
    (publication: Publication): Publication[] => {
      return data.publications.filter((p) => publication.citations.includes(p.id))
    },
    [data.publications]
  )

  // Render a single publication card
  const renderPublicationCard = (publication: Publication, level = 0) => {
    const isExpanded = expandedPublications.has(publication.id)
    const isFocused = publication.id === focusedPublicationId
    const citing = getCitingPublications(publication)
    const cited = getCitedPublications(publication)

    return (
      <Card
        key={publication.id}
        className={`mb-4 transition-all duration-300 ${isFocused ? 'shadow-lg ring-2 ring-blue-500' : 'shadow-sm'}`}
        style={{ marginLeft: `${level * 20}px` }}
      >
        <CardHeader className="cursor-pointer pb-2" onClick={() => toggleExpand(publication.id)}>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg leading-tight">{publication.title}</CardTitle>
              <CardDescription className="mt-1 text-sm">
                {publication.authors.join(', ')} ({publication.year}) - {publication.venue}
              </CardDescription>
            </div>
            <div className="ml-2 flex shrink-0 items-center space-x-1">
              {publication.metrics?.citations && (
                <Badge variant="secondary">{publication.metrics.citations} citations</Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={(e) => {
                  e.stopPropagation()
                  focusPublication(publication.id)
                }}
              >
                <IconCrosshair size={16} />
              </Button>
            </div>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="pt-2">
            {showAbstracts && (
              <p className="mb-3 text-sm italic text-gray-600 dark:text-gray-400">
                {publication.abstract}
              </p>
            )}
            <div className="mb-3 flex flex-wrap gap-1">
              {publication.keywords.map((keyword, idx) => (
                <Badge key={idx} variant="outline">
                  {keyword}
                </Badge>
              ))}
            </div>
            <div className="mb-4 flex items-center space-x-2 text-sm">
              {publication.doi && (
                <a
                  href={`https://doi.org/${publication.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:underline"
                >
                  <IconLink size={14} className="mr-1" /> DOI
                </a>
              )}
              {publication.url && (
                <a
                  href={publication.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:underline"
                >
                  <IconBook size={14} className="mr-1" /> URL
                </a>
              )}
            </div>

            {level < depth && (
              <div className="space-y-3">
                {citing.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Cited By ({citing.length}):
                    </h4>
                    {citing.map((p) => renderPublicationCard(p, level + 1))}
                  </div>
                )}
                {cited.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Cites ({cited.length}):
                    </h4>
                    {cited.map((p) => renderPublicationCard(p, level + 1))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    )
  }

  // Group publications based on selected criteria
  const groupedPublications = useMemo(() => {
    if (!data.groupBy || data.groupBy === 'none') {
      return [{ group: 'All Publications', publications: searchResults }]
    }

    const groups: Record<string, Publication[]> = {}

    searchResults.forEach((pub) => {
      let key = ''
      if (data.groupBy === 'year') {
        key = pub.year.toString()
      } else if (data.groupBy === 'venue') {
        key = pub.venue
      } else if (data.groupBy === 'keywords') {
        // For keywords, a publication might belong to multiple groups
        pub.keywords.forEach((kw) => {
          if (!groups[kw]) groups[kw] = []
          groups[kw].push(pub)
        })
        return // Skip default push for keyword grouping
      }

      if (!key) key = 'Other' // Fallback group
      if (!groups[key]) groups[key] = []
      groups[key].push(pub)
    })

    return Object.entries(groups)
      .map(([group, publications]) => ({ group, publications }))
      .sort((a, b) =>
        data.groupBy === 'year'
          ? parseInt(b.group) - parseInt(a.group)
          : a.group.localeCompare(b.group)
      )
  }, [searchResults, data.groupBy])

  return (
    <Card className="citation-network my-4 w-full">
      <CardHeader>
        <CardTitle>{data.title}</CardTitle>
        <CardDescription>{data.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <Input
            placeholder="Search title, author, keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <div className="flex items-center space-x-2">
            <Label htmlFor={`depth-slider-${data.title.replace(/\s+/g, '-')}`}>Depth:</Label>
            <Slider
              id={`depth-slider-${data.title.replace(/\s+/g, '-')}`}
              min={1}
              max={5}
              step={1}
              value={[depth]}
              onValueChange={(value) => setDepth(value[0])}
              className="w-32"
            />
            <span className="text-sm">{depth}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id={`abstract-switch-${data.title.replace(/\s+/g, '-')}`}
              checked={showAbstracts}
              onCheckedChange={setShowAbstracts}
            />
            <Label htmlFor={`abstract-switch-${data.title.replace(/\s+/g, '-')}`}>
              Show Abstracts
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={resetFocus} disabled={!focusedPublicationId}>
            Reset Focus
          </Button>
        </div>

        <div className="max-h-[600px] overflow-y-auto pr-2">
          {focusedPublication ? (
            renderPublicationCard(focusedPublication)
          ) : groupedPublications.length > 0 ? (
            groupedPublications.map(({ group, publications }) => (
              <div key={group} className="mb-6">
                {data.groupBy !== 'none' && (
                  <h3 className="text-md mb-3 border-b pb-1 font-semibold">{group}</h3>
                )}
                {publications.map((pub) => renderPublicationCard(pub))}
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">No publications match your search.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default CitationNetwork
